import { cn } from './cn.js';
/**
 * Shared visual treatment for Text input, Textarea and Select
 * (README.md > Atoms: "Select: Same box as text input"). Focus uses its
 * own border-plus-glow treatment rather than the generic FOCUS_RING —
 * the Text input atom documents that explicitly, distinct from the
 * generic outline ring used on Button/Checkbox/Radio/Switch. On `:focus`
 * rather than `:focus-visible`, because a field being typed into is focused
 * however it was reached, and the caret alone is easy to lose.
 *
 * `outline-hidden`, not `outline-none`: in Tailwind v4 the two differ exactly
 * where it matters here. The glow is a box-shadow, and forced-colours mode
 * (Windows High Contrast) drops shadows and repaints borders in one system
 * colour — so the border change and the glow both vanish there, and a field
 * would show no focus at all. `outline-hidden` hides the outline normally and
 * leaves a transparent 2px one under forced colours, which the system then
 * paints in its highlight colour.
 *
 * Placeholders are `text-muted`, not `text-disabled`, and the handoff is
 * cited on both sides of that: its Text input spec names #A1A1AA
 * (text-disabled), while its colour table gives text-muted the role
 * "Labels, captions, placeholders". The table wins, per the handoff's own
 * tie-break (see theme.css, "Source of truth"), and it is also the only
 * one of the two that is legible: #A1A1AA on white is 2.56:1, under the
 * 4.5:1 that WCAG 2.2 AA asks of text this size, where #71717A is 4.83:1.
 * Placeholder text gets no exemption — only genuinely inactive controls
 * do, which is why `disabled:` below still uses text-disabled.
 */
export function fieldClasses(hasError) {
    return cn('w-full rounded-sm border-[1.5px] bg-surface text-body text-text placeholder:text-text-muted', 'transition-colors duration-fast', hasError ? 'border-error' : 'border-border-strong', 'focus:outline-hidden focus:border-primary focus:ring-4 focus:ring-primary/18', 'disabled:border-border disabled:bg-surface-secondary disabled:text-text-disabled');
}
/**
 * The same focus treatment, for a field built from parts: a box that holds
 * the control rather than being it, like the Markdown editor's gutter and
 * text area. The box takes the border and glow when anything in it has
 * focus, and the forced-colours outline with them.
 */
export const fieldFocusWithinClasses = 'focus-within:outline-hidden focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/18';
/**
 * Box height for the three field atoms.
 *
 * `md` is the default rather than `lg`, for one reason that shows up in every
 * form: Button already defaults to `md`, so a field sitting next to its own
 * submit button stood 8px taller than it and the row read as misaligned. `lg`
 * stays for the places a field is the only thing on the screen, the sign-in
 * email being the case it was drawn for.
 */
export function fieldSizeClasses(size) {
    return size === 'lg' ? 'h-control-lg px-4' : 'h-control-md px-3.5';
}
/**
 * Label to control, and control to message. 6px rather than the handoff form
 * spec's 8px: the label is 13px on a 1.2 line box, and once the box came down
 * to 40px an 8px gap read as separation rather than as attachment.
 */
export const fieldStackClasses = 'flex flex-col gap-1.5';
/**
 * What deliberately did NOT shrink, so a later density pass does not "finish
 * the job" and break something:
 *
 * - Button stays on 32/40/48. It was already the shorter of the two scales,
 *   and it is what the fields were brought down to meet.
 * - Checkbox, RadioGroup and IconButton keep `size-touch-target-min` (44px).
 *   That is the hit area, not the drawn control: the visible box is 32px and
 *   already smaller than the target around it. Shrinking the target to match
 *   the paint is the one change here that would be an accessibility
 *   regression rather than a density win.
 * - The 44px floor also means `control-md` at 40px is the shortest a real
 *   input should get. There is no `control-sm` field for this reason.
 */
export const fieldLabelClasses = 'text-label font-semibold text-text-strong';
//# sourceMappingURL=field-styles.js.map